import React, { useEffect } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { Offer, PopUpProductSequence, PopUpSequenceProduct } from 'common/contracts';
import { EPopupSubType, ESaleDiscountDisplayType } from 'constants/enums';
import Decimal from 'decimal.js';
import { v4 as uuidv4 } from 'uuid';

import {
  DEFAULT_VALUES,
  EPopupTriggersAction,
  PopupsFormFields
} from '../PopupsForm.consts';
import { ProductEntry } from './usePopupProducts';

interface IProgressBarFormValues {
  progressBarId: string | null;
  progressBarPoints: number | null;
}

interface IUsePopupFormInitProps {
  form: UseFormReturn<PopupsFormFields>;
  edit: boolean;
  dup: boolean;
  popupId: string | undefined;
  defaultOfferUiId: string;
  currentOffer: Offer | undefined;
  setDailyProducts: (products: ProductEntry[]) => void;
  setPostProducts: (products: ProductEntry[]) => void;
  setProgressBarFormValues: React.Dispatch<React.SetStateAction<IProgressBarFormValues>>;
}

/**
 * Handles form initialization for edit/duplicate/create flows of the Popup form.
 *
 * @example
 * usePopupFormInit({ form, edit, dup, popupId, defaultOfferUiId, currentOffer, setDailyProducts, setPostProducts });
 */
export const usePopupFormInit = ({
  form,
  edit,
  dup,
  popupId,
  defaultOfferUiId,
  currentOffer,
  setDailyProducts,
  setPostProducts,
  setProgressBarFormValues
}: IUsePopupFormInitProps): void => {
  useEffect(() => {
    if (!popupId && defaultOfferUiId) {
      form.setValue('offerUiId', defaultOfferUiId, {
        shouldDirty: false,
        shouldValidate: true
      });
    }

    if ((edit || dup) && currentOffer) {
      const { isDailySubType, products, formValues } = prepareInitialFormValues(
        currentOffer,
        dup,
        edit,
        defaultOfferUiId
      );
      setDailyProducts(isDailySubType ? products : []);
      setPostProducts(isDailySubType ? [] : products);
      setProgressBarFormValues({
        progressBarPoints:
          currentOffer.productsSequence?.[0]?.progressBarPoints?.[0]?.points ?? null,
        progressBarId:
          currentOffer.productsSequence?.[0]?.progressBarPoints?.[0]?.barId ?? null
      });
      form.reset(formValues);
    }
  }, [
    edit,
    dup,
    currentOffer,
    form,
    popupId,
    defaultOfferUiId,
    setDailyProducts,
    setPostProducts,
    setProgressBarFormValues
  ]);
};

const prepareInitialFormValues = (
  result: Offer,
  dup: boolean,
  edit: boolean,
  defaultOfferUiId: string
): { isDailySubType: boolean; products: ProductEntry[]; formValues: any } => {
  const priceCents = result.productsSequence?.[0]?.priceInUsdCents ?? 0;
  const isDailySubType = result.subType === EPopupSubType.DAILY_BONUS;
  const isStoreRefresh = result.subType === EPopupSubType.STORE_REFRESH;
  const displayRule = result.displayRule;

  const productsSequence = result.productsSequence as PopUpProductSequence[];

  const products: ProductEntry[] = productsSequence
    .filter((seq) => seq.products.length > 0)
    .map((seq) => {
      const product = seq.products[0] as PopUpSequenceProduct;
      return {
        id: uuidv4(),
        productId: product.product?.productId ?? '',
        quantity: product.quantity ?? 0,
        displayName: product.product?.displayName ?? '',
        image: product.product?.images?.[0]?.url ?? '',
        quantityDisplay: product?.quantityDisplay
      };
    });

  const formValues = {
    ...DEFAULT_VALUES,
    ...result,
    active: dup || edit ? result.active : true,
    name: dup ? `${result.name}_copy` : result.name,
    displayName: `${dup ? result.displayName ?? result.name : result.displayName ?? result.name}${dup ? '_copy' : ''}`,
    publisherOfferId: dup
      ? `${result.publisherOfferId}_copy`
      : result.publisherOfferId,
    offerUiId:
      result.offerUi?._id || result.offerUi?.offerUiId || defaultOfferUiId,
    price: isDailySubType ? 0 : new Decimal(priceCents).div(100).toNumber(),
    setAsFree: isDailySubType ? false : priceCents === 0,
    products,
    salePercentage: result.productSale?.sale ?? DEFAULT_VALUES.salePercentage,
    salePercentageDisplayType:
      (result.productSale?.type as ESaleDiscountDisplayType) ??
      DEFAULT_VALUES.salePercentageDisplayType,
    discount: result.priceDiscount?.discount ?? DEFAULT_VALUES.discount,
    priceDiscountType:
      result.priceDiscount?.type ?? DEFAULT_VALUES.priceDiscountType,
    subType: result.subType ?? DEFAULT_VALUES.subType,
    triggersAction: isStoreRefresh
      ? EPopupTriggersAction.STORE_REFRESH
      : EPopupTriggersAction.PURCHASE_COMPLETED,
    triggersFrequency: deriveTriggersFrequency(isDailySubType, isStoreRefresh, displayRule, result),
    coolDownInMinutes: isStoreRefresh
      ? (displayRule?.cooldownInMinutes ?? 1)
      : (result.coolDownInHours || 0) * 60,
    triggersOfferExternalIds: isStoreRefresh
      ? []
      : (result.triggers?.[0]?.rules?.[0]?.value ?? [])
  };

  return { isDailySubType, products, formValues };
};

const deriveTriggersFrequency = (
  isDailySubType: boolean,
  isStoreRefresh: boolean,
  displayRule: Offer['displayRule'],
  result: Offer
): number => {
  if (isDailySubType) return 1;
  if (isStoreRefresh) return displayRule?.frequency ?? 1;
  return result.triggers?.[0]?.every ?? 1;
};
